import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import { useLazyGetPaymentAccountsQuery } from '@app/api/PaymentAccounts';
import {
  EDIT_PAYMENT_ACCOUNT_PATH,
  MOBILE,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_DELETE,
  RIGHTS_IS_EDIT,
  RIGHTS_IS_ON,
  RIGHTS_PAYMENT_ACCOUNTS,
  WARNING,
} from '@app/constants';
import { useAccessRights, useUserPrint, useWindowSize } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { changeStandardPaginationUi } from '@app/utils';

export const usePaymentAccounts = () => {
  const { activeBranchId } = useUserPrint();

  const [getPaymentAccounts] = useLazyGetPaymentAccountsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState<ModelPaymentAccount[]>(
    []
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { showNotification } = useContext(NotificationContext);

  const navigate = useNavigate();

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_PAYMENT_ACCOUNTS,
    RIGHTS_IS_ON
  );
  const { hasUserRight: hasUserRightToCreatePaymentsAccount } = useAccessRights(
    RIGHTS_PAYMENT_ACCOUNTS,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToEditPaymentsAccount } = useAccessRights(
    RIGHTS_PAYMENT_ACCOUNTS,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: hasUserRightToDeletePaymentsAccount } = useAccessRights(
    RIGHTS_PAYMENT_ACCOUNTS,
    RIGHTS_IS_DELETE
  );

  const getPaymentAccountsHandler = useCallback(async () => {
    if (!activeBranchId) return;

    try {
      setIsLoading(true);

      const result = await getPaymentAccounts({
        ActiveBranchId: activeBranchId,
        branchId: activeBranchId,
        includeDeleted: true,
      }).unwrap();

      setPaymentAccounts(result);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setIsLoading(false);
    }
  }, [activeBranchId, getPaymentAccounts, showNotification]);

  useEffect(() => {
    getPaymentAccountsHandler();
  }, [activeBranchId, getPaymentAccountsHandler]);

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [paymentAccounts, isMobile]);

  const handleEdit = (Id: number) => {
    navigate(`${EDIT_PAYMENT_ACCOUNT_PATH}?id=${Id}`);
  };

  const handleChange = (page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);
  };

  return {
    isLoading,
    paymentAccounts,
    handleEdit,
    isMobile,
    hasUserRightForModuleView,
    hasUserRightToCreatePaymentsAccount,
    hasUserRightToEditPaymentsAccount,
    hasUserRightToDeletePaymentsAccount,
    handleChange,
    pageNumber,
    rowsPerPage,
  };
};
